import React from 'react'

// SSR stub for @radix-ui — passthrough components for server rendering.

function Passthrough({ children }: any) { return children ?? null }

function DivWrapper(props: any) {
  const { asChild, children, forceMount, ...rest } = props
  if (asChild && React.isValidElement(children)) return React.cloneElement(children, rest)
  return <div {...rest}>{children}</div>
}

function ButtonWrapper(props: any) {
  const { asChild, children, forceMount, ...rest } = props
  if (asChild && React.isValidElement(children)) return React.cloneElement(children, rest)
  return <button type='button' {...rest}>{children}</button>
}

function AccordionContentWrapper(props: any) {
  const { asChild, children, forceMount, ...rest } = props
  return <div data-state='closed' {...rest}>{children}</div>
}

function AccordionTriggerWrapper(props: any) {
  const { asChild, children, forceMount, ...rest } = props
  if (asChild && React.isValidElement(children)) return React.cloneElement(children, rest)
  return <button type='button' data-state='closed' {...rest}>{children}</button>
}

export const Root = DivWrapper
export const Item = DivWrapper
export const Header = DivWrapper
export const Trigger = AccordionTriggerWrapper
export const Content = AccordionContentWrapper
export const Portal = Passthrough
export const Overlay = () => null
export const Close = ButtonWrapper
export const Title = DivWrapper
export const Description = DivWrapper
export const Cancel = ButtonWrapper
export const Action = ButtonWrapper
export const Group = DivWrapper
export const Label = DivWrapper
export const Separator = () => <hr />
export const CheckboxItem = DivWrapper
export const RadioGroup = DivWrapper
export const RadioItem = DivWrapper
export const ItemIndicator = Passthrough
export const Sub = DivWrapper
export const SubTrigger = ButtonWrapper
export const SubContent = DivWrapper
export const Arrow = () => null
export const List = DivWrapper
export const Link = (props: any) => {
  const { asChild, children, ...rest } = props
  if (asChild && React.isValidElement(children)) return React.cloneElement(children, rest)
  return <a {...rest}>{children}</a>
}
export const Viewport = DivWrapper
export const Indicator = () => null
export const Value = Passthrough
export const Icon = Passthrough
export const ScrollUpButton = () => null
export const ScrollDownButton = () => null
export const ItemText = Passthrough
export const Thumb = DivWrapper
export const Track = DivWrapper
export const Range = DivWrapper
export const Image = (props: any) => <img {...props} />
export const Fallback = DivWrapper
export const Provider = Passthrough

export function Slot({ children, ...props }: any) {
  if (React.isValidElement(children)) return React.cloneElement(children, { ...props, ...(children.props as any) })
  return children ?? null
}
export const Slottable = Passthrough
export function createSlot() { return Slot }
export function createSlottable() { return Slottable }

export const Primitive = new Proxy({} as any, {
  get(_, tag: string) {
    return React.forwardRef((props: any, ref: any) => {
      const { asChild, ...rest } = props
      if (asChild && React.isValidElement(props.children)) return React.cloneElement(props.children, { ...rest, ref })
      return React.createElement(tag, { ...rest, ref })
    })
  },
})

export function createContext(_rootName: string, defaultContext?: any) {
  const Ctx = React.createContext(defaultContext)
  const ProviderComp = ({ children, ...value }: any) => <Ctx.Provider value={value}>{children}</Ctx.Provider>
  const useCtx = () => React.useContext(Ctx) ?? defaultContext ?? {}
  return [ProviderComp, useCtx] as const
}
export function createContextScope(..._args: any[]) {
  return [
    (_name: string, defaults?: any) => {
      const Ctx = React.createContext(defaults)
      return [({ children, ...value }: any) => <Ctx.Provider value={value}>{children}</Ctx.Provider>, () => defaults ?? {}]
    },
    () => {},
  ] as any
}

export function composeRefs(...refs: any[]) {
  return (node: any) => refs.forEach((r) => (typeof r === 'function' ? r(node) : r && (r.current = node)))
}
export const useComposedRefs = composeRefs
export const DismissableLayer = DivWrapper
export const FocusScope = Passthrough
export function useFocusGuards() {}
export function Presence({ children, present }: any) {
  return present !== false ? (typeof children === 'function' ? children({ present: true }) : children) : null
}
export function createCollection() {
  return { Provider: Passthrough, Slot: DivWrapper, ItemSlot: DivWrapper, useCollection: () => [] }
}
export function createMenuScope() { return () => ({}) }
export function createPopperScope() { return () => ({}) }
export function createRovingFocusGroupScope() { return () => ({}) }
export function useId() { return 'ssr-id' }
export function useCallbackRef(fn: any) { return fn }
export function useControllableState({ defaultValue, value }: any) { return [value ?? defaultValue, () => {}] }
export function useLayoutEffect() {}
export function useSize() { return undefined }
export function usePrevious(value: any) { return value }
export function useEscapeKeydown() {}
export function useDirection() { return 'ltr' }
export function DirectionProvider({ children }: any) { return children }
export function useIsHydrated() { return false }
export const VisuallyHidden = (props: any) => (
  <span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0,0,0,0)' }} {...props} />
)
export function composeEventHandlers(original: any, handler: any) { return handler || original }
export const RemoveScroll = Passthrough

export default Passthrough
